import React from "react";
import "./home.css";
import Navbar from "./navbar";
import Footer from "./footer";
import { ArrowRight } from "lucide-react";

const Home = () => {
  return (
    <>
      {/* NAVBAR */}
      <header>
        <Navbar />
      </header>

      <div style={{ paddingTop: "80px" }}>
        {/* HERO SECTION */}
        <section className="features-hero pb-5">
          <div className="container">
            <div className="row align-items-center min-vh-75 justify-content-center text-center text-md-start">
              <div className="col-12 col-md-6 order-2 order-md-1 mb-4 mb-md-0">
                <div className="badge-wrapper mb-3">
                  <span className="badge-text">AI-Powered Solution</span>
                </div>

                <h1 className="hero-title mt-2">
                  Smart Billing for{" "}
                  <span className="gradient-text">All Businesses</span>
                </h1>

                <p className="hero-subtitle mt-3">
                  Simplify invoicing, inventory, payments, and GST compliance
                  with one powerful platform designed for Indian SMEs.
                </p>

                <div className="hero-actions mt-4 d-flex justify-content-center justify-content-md-start gap-3">
                  <a href="/login">
                    <button className="cta-btn cta-btn-primary">
                      Start Free Trial <ArrowRight size={18} className="ms-2" />
                    </button>
                  </a>
                </div>

                <div className="trust-indicators mt-4 d-flex  d-none d-md-flex  flex-wrap gap-3">
                  <div className="trust-item">
                    <span className="trust-icon">✓</span> No credit card
                    required
                  </div>
                  <div className="trust-item">
                    <span className="trust-icon">✓</span> 14-day free trial
                  </div>
                  <div className="trust-item">
                    <span className="trust-icon">✓</span> 24/7 support
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6 order-1 order-md-2 text-center py-3">
                <img
                  src="/assets/img/header-img.png"
                  alt="Dashboard"
                  className="hero-image img-fluid"
                />
              </div>
            </div>
          </div>
        </section>

        {/* PRODUCT PREVIEW */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5">
              <h2 className="heading fw-bold display-6 mt-5">
                Everything You Need, At One Place
              </h2>
              <p className="text-muted">
                Real-time business overview, insights, and transaction tracking
              </p>
            </div>

            <div className="row align-items-center justify-content-center text-center text-md-start">
              <div className="col-lg-6 mb-4 mb-lg-0">
                <span className="badge bg-secondary bg-opacity-10 text-secondary mb-2">
                  Real-Time Dashboard
                </span>

                <h3 className="fs-3 fw-bold mb-3">
                  Smart Dashboard for Smart Decisions
                </h3>

                <p className="text-muted">
                  Track sales, purchases, inventory, and payments in real time.
                  Visual reports help you understand your business instantly.
                </p>

                <ul className="list-unstyled mt-3">
                  {[
                    "Sales vs Purchases insights",
                    "Top selling items analysis",
                    "Due & payment tracking",
                  ].map((item, i) => (
                    <li
                      key={i}
                      className="mb-2 d-flex gap-2 justify-content-center justify-content-md-start text-center text-md-start"
                    >
                      <span className="tick">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="col-lg-6 text-center">
                <img
                  src="/assets/img/dashboard.png"
                  alt="Dashboard Preview"
                  className="img-fluid"
                />
              </div>
            </div>
          </div>
        </section>

        {/* CORE OPERATIONS */}
        <section className="py-6  position-relative overflow-hidden bg-white">
          <div className="container">
            {/* Section Header */}
            <div className="text-center my-5">
              <h2 className="heading fw-bold display-6 ">
                Master Your Products & Inventory
              </h2>
              <p className="text-muted mt-3">
                Keep your stock levels optimal and your product information
                flawless.
              </p>
            </div>

            {/* Feature Cards */}
            <div className="row g-4 justify-content-center mb-5">
              {[
                {
                  title: "Centralized Item Management",
                  desc: "Add, edit, and categorize all your products with detailed SKU, pricing, and tax info.",
                  svg: (
                    <svg
                      width="64"
                      height="64"
                      viewBox="0 0 24 24"
                      fill="#1e3a8a"
                    >
                      <path d="M3 7v13h18V7H3zm2 2h14v9H5v-9zm2-5v2h10V4H7z" />
                    </svg>
                  ),
                },
                {
                  title: "Real-Time Stock Tracking",
                  desc: "Track inventory across multiple locations in real-time for perfect accuracy.",
                  svg: (
                    <svg
                      width="64"
                      height="64"
                      viewBox="0 0 24 24"
                      fill="#1e3a8a"
                    >
                      <path d="M3 13h2v-2H3v2zm4 0h2v-4H7v4zm4 0h2v-6h-2v6zm4 0h2v-8h-2v8zm4 0h2v-10h-2v10z" />
                    </svg>
                  ),
                },
                {
                  title: "Smart Low Stock Alerts",
                  desc: "Get notifications before running out of stock to reorder on time.",
                  svg: (
                    <svg
                      width="64"
                      height="64"
                      viewBox="0 0 24 24"
                      fill="#1e3a8a"
                    >
                      <path d="M12 22c1.104 0 2-.896 2-2h-4c0 1.104.896 2 2 2zm6-6V9c0-3.309-2.691-6-6-6S6 5.691 6 9v7l-2 2v1h16v-1l-2-2z" />
                    </svg>
                  ),
                },
                {
                  title: "Accurate Stock Adjustment",
                  desc: "Easily reconcile physical inventory with your system records.",
                  svg: (
                    <svg
                      width="64"
                      height="64"
                      viewBox="0 0 24 24"
                      fill="#1e3a8a"
                    >
                      <path d="M9 16.172l-3.586-3.586-1.414 1.414L9 19 20 8l-1.414-1.414z" />
                    </svg>
                  ),
                },
              ].map((f, i) => (
                <div key={i} className="col-12 col-md-6 col-lg-3">
                  <div className="feature-card h-100 px-5 py-4 text-center rounded-4 bg-white position-relative overflow-hidden">
                    {/* Icon */}
                    <div className="my-2 mx-auto d-flex align-items-center justify-content-center icon-wrapper">
                      {f.svg}
                    </div>
                    {/* Title */}
                    <h5 className="fw-bold fs-4">{f.title}</h5>
                    {/* Description */}
                    <p className="text-muted">{f.desc}</p>

                    {/* Paper Stack Layers */}
                    <span className="paper-layer top-left layer1"></span>
                    <span className="paper-layer top-left layer2"></span>
                    <span className="paper-layer bottom-right layer3"></span>
                    <span className="paper-layer bottom-right layer4"></span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FEATURES */}
        <section className="py-5 bg-white" id="features">
          <div className="container">
            <div className="my-5 text-center">
              <h2 className="heading fw-bold mb-2 display-6">
                Simple, Powerful & Built for Business
              </h2>
              <p className="text-muted col-lg-7 mx-auto">
                Manage inventory, billing, payments, and reports with accuracy
                and speed.
              </p>
            </div>

            <div className="row g-4 rounded-4 p-lg-5">
              {[
                {
                  title: "Inventory",
                  items: [
                    "Centralized Item Management",
                    "Real-Time Stock Tracking",
                    "Smart Low Stock Alerts",
                    "Accurate Stock Adjustment",
                    "Add Bulk Item",
                  ],
                },
                {
                  title: "Billing & Payments",
                  items: [
                    "POS & Quick Billing",
                    "Unified Contact Management",
                    "Seamless Returns Process",
                    "Integrated Payment Management",
                    "Party Ledgers",
                  ],
                },
                {
                  title: "Reports & Purchases",
                  items: [
                    "Purchase Order Management",
                    "Efficient Purchase Entry",
                    "Supplier Transaction History",
                    "Comprehensive Sales Reports",
                    "Detailed Purchase Reports",
                    "Business Reports",
                  ],
                },
              ].map((group, i) => (
                <div key={i} className="col-lg-4">
                  <div className="feature-group h-100">
                    <h6 className="group-title">{group.title}</h6>
                    <ul className="feature-list">
                      {group.items.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mb-3">
              <a
                href="/features"
                className="cta-btn cta-btn-primary text-white"
              >
                Explore More <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </section>

        {/* ANALYTICS */}
        <section className="py-5 bg-light">
          <div className="container">
            {/* Header */}
            <div className="text-center my-5">
              <h2 className="heading fw-bold display-6">
                Streamline Every Part of Your Business
              </h2>
              <p
                className="text-muted mt-3 mx-auto fs-6"
                style={{ maxWidth: "720px" }}
              >
                Manage sales, operations, customer relations, and analytics from
                one unified platform.
              </p>
            </div>

            {/* Features */}
            <div className="row g-5 text-center justify-content-center">
              {/* Feature 1 */}
              <div className="col-md-4 feature-item px-4">
                <svg
                  width="56"
                  height="56"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#0c1e7e"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mb-4"
                >
                  <rect x="3" y="7" width="18" height="13" rx="2" />
                  <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                </svg>
                <h5 className="fw-semibold mb-2 fs-5">
                  Centralized Management
                </h5>
                <p className="text-muted fs-6">
                  Oversee sales, inventory, and finances from a single system.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="col-md-4 feature-item px-4">
                <svg
                  width="56"
                  height="56"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#0c1e7e"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mb-4"
                >
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06A2 2 0 1 1 3.4 19.4l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H2a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06A2 2 0 1 1 5.23 3.4l.06.06a1.65 1.65 0 0 0 1.82.33h.09A1.65 1.65 0 0 0 8 2.28V2a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.09a1.65 1.65 0 0 0 1.82-.33l.06-.06A2 2 0 1 1 20.6 5.23l-.06.06a1.65 1.65 0 0 0-.33 1.82v.09A1.65 1.65 0 0 0 21.72 8H22a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                <h5 className="fw-semibold mb-2 fs-5">Process Automation</h5>
                <p className="text-muted fs-6">
                  Automate workflows to reduce effort and increase efficiency.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="col-md-4 feature-item px-4">
                <svg
                  width="56"
                  height="56"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#0c1e7e"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mb-4"
                >
                  <path d="M3 3v18h18" />
                  <path d="M18 9l-5 5-4-4-3 3" />
                </svg>
                <h5 className="fw-semibold mb-2 fs-5">Business Insights</h5>
                <p className="text-muted fs-6">
                  Make smarter decisions with real-time performance data.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SUPPLIER & PURCHASE */}
        <section className="py-5 bg-white">
          <div className="container">
            {/* Header */}
            <div className="text-center mb-5">
              <h2 className="heading fw-bold display-6">
                Streamline Purchasing & Supplier Relations
              </h2>
              <p className="text-muted">
                Order smarter, manage costs, and maintain a smooth supply chain.
              </p>
            </div>

            {/* Accordion */}
            <div
              className="accordion custom-accordion mx-auto"
              id="purchaseAccordion"
              style={{ maxWidth: "1000px" }}
            >
              {[
                {
                  id: "one",
                  title: "Purchase Order Management",
                  desc: "Create, approve, and send purchase orders to suppliers with full clarity and control.",
                },
                {
                  id: "two",
                  title: "Efficient Purchase Entry",
                  desc: "Quickly record incoming stock and update inventory without manual effort.",
                },
                {
                  id: "three",
                  title: "Supplier Transaction History",
                  desc: "View complete order history and supplier interactions in one place.",
                },
                {
                  id: "four",
                  title: "Supplier Relationship Management",
                  desc: "Maintain strong supplier relationships with organized data and insights.",
                },
              ].map((item, i) => (
                <div className="accordion-item" key={i}>
                  <h2 className="accordion-header">
                    <button
                      className={`accordion-button ${
                        i !== 0 ? "collapsed" : ""
                      }`}
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#collapse-${item.id}`}
                    >
                      {item.title}
                    </button>
                  </h2>
                  <div
                    id={`collapse-${item.id}`}
                    className={`accordion-collapse collapse ${
                      i === 0 ? "show" : ""
                    }`}
                    data-bs-parent="#purchaseAccordion"
                  >
                    <div className="accordion-body text-muted">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="cta-section text-center py-5">
          <div className="container">
            <h2 className="heading fw-bold text-white">
              Start Managing Your Business Smarter
            </h2>
            <p className="text-white-50 mt-2">
              Simple, fast & GST-ready business automation
            </p>

            <a href="/login">
              <button className="btn btn-white mt-3 fw-semibold">
                Start Free Trial <ArrowRight size={18} className="ms-2" />
              </button>
            </a>
          </div>
        </section>
      </div>
      {/* FOOTER */}
      <footer>
        <Footer />
      </footer>
    </>
  );
};

export default Home;
